import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  selectIsOnCancelPressed,
  selectIsOnConfirmPressed,
  setIsOnCancelPressed,
  setIsOnConfirmPressed,
  setIsShowEstimationInterruptModal,
} from "../../../redux/features/solar-estimation/solarEstimationSlice";
import { selectIsDarkMode } from "../../../redux/features/theme/theme-slice";
import {
  selectCountry,
  setCountry,
} from "../../../redux/features/user/user-slice";
import { ECountry } from "../../oversight-core/enums/country";
import { AppRoute } from "../../oversight-core/interfaces/app-routes";
import AppDropDown from "../../oversight-core/ui-elements/app-dropdown/app-drop-down";
import AppButton from "../../oversight-core/ui-elements/buttons/app-button/app-button";
import CountryFlag from "../../oversight-core/ui-elements/country-flag/country-flag";
import MaterialIcon from "../../oversight-core/ui-elements/material-icon/material-icon";
import { isOnEstimation } from "../../oversight-core/utils/solarEstimationHelper";
import translate from "../../oversight-core/utils/translate";
import Logo from "../logo/logo";

interface PublicNavBarProps {
  onMenuClick?: () => void;
  onMobileMenuClick?: () => void;
}

const PublicNavBar = ({
  onMenuClick,
  onMobileMenuClick,
}: PublicNavBarProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;

  const country = useSelector(selectCountry);
  const isOnConfirmPressed = useSelector(selectIsOnConfirmPressed);
  const isOnCancelPressed = useSelector(selectIsOnCancelPressed);
  const isDarkMode = useSelector(selectIsDarkMode);

  const [navigateOnInterruption, setNavigateOnInterruption] = useState("");

  useEffect(() => {
    if (isOnConfirmPressed && navigateOnInterruption) {
      dispatch(setIsShowEstimationInterruptModal(false));
      dispatch(setIsOnConfirmPressed(false));
      setNavigateOnInterruption("");
      navigate(navigateOnInterruption);
    }
  }, [isOnConfirmPressed, navigateOnInterruption]);

  useEffect(() => {
    if (isOnCancelPressed && navigateOnInterruption) {
      setNavigateOnInterruption("");
      dispatch(setIsOnCancelPressed(false));
    }
  }, [isOnCancelPressed, navigateOnInterruption]);

  return (
    <>
      <Container fluid className="bg-nav-bar py-2 px-4">
        <Row className="align-items-center justify-content-between">
          <Col>
            <Row className="align-items-center">
              <Col className="col-auto d-none d-xl-block">
                <MaterialIcon
                  icon="menu"
                  color="var(--color-menu-icon)"
                  className="cursor-pointer"
                  size={26}
                  onClick={onMenuClick}
                />
              </Col>
              <Col className="col-auto d-block d-xl-none">
                <MaterialIcon
                  icon="menu"
                  color="var(--color-menu-icon)"
                  className="cursor-pointer"
                  size={26}
                  onClick={onMobileMenuClick}
                />
              </Col>
              <Col className="col-auto d-none d-lg-block">
                <a
                  href="https://www.chlorideexide.com/batteryfinder"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  <div
                    className={`bg-primary border border-2 border-primary rounded-1 d-flex align-items-center justify-content-center text-white py-2 px-4 font-weight-600 font-size-12 cursor-pointer`}
                  >
                    Battery Finder
                  </div>
                </a>
              </Col>
              <Col className="col-auto d-none d-lg-block">
                <a
                  href="https://www.chlorideexide.com/category/all-products"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  <div
                    className={`bg-danger border border-2 border-danger rounded-1 d-flex align-items-center justify-content-center text-white py-2 px-4 font-weight-600 font-size-12 cursor-pointer`}
                  >
                    Shop
                  </div>
                </a>
              </Col>
              <Col className="col-auto d-none d-lg-block">
                <a href="tel:+254719080000" className="text-decoration-none">
                  <div
                    className={`${
                      isDarkMode ? "bg-white" : "bg-none border-black"
                    } border border-2 rounded-1 d-flex align-items-center justify-content-center text-black py-2 px-4 font-weight-600 font-size-12 cursor-pointer`}
                  >
                    Dial a Battery
                  </div>
                </a>
              </Col>
            </Row>
          </Col>
          <Col className="col-auto">
            <Row className="row-cols-auto align-items-center gx-3">
              <Col className="pe-0">
                <AppButton
                  variant="transparent"
                  text="Sign Up"
                  className="font-weight-600 font-size-12 text-color-mode"
                  onClick={() => {
                    if (isOnEstimation(pathname)) {
                      setNavigateOnInterruption(AppRoute.REGISTER);
                      dispatch(setIsShowEstimationInterruptModal(true));
                      return;
                    }

                    navigate(AppRoute.REGISTER);
                  }}
                />
              </Col>
              <Col>
                <div
                  className={`bg-primary border-2 border-primary rounded-2 d-flex align-items-center justify-content-center text-white py-2 px-4 font-weight-600 font-size-12 cursor-pointer`}
                  onClick={() => {
                    if (isOnEstimation(pathname)) {
                      setNavigateOnInterruption(AppRoute.LOGIN);
                      dispatch(setIsShowEstimationInterruptModal(true));
                      return;
                    }

                    navigate(AppRoute.LOGIN);
                  }}
                >
                  Login
                </div>
              </Col>
              <Col>
                <div
                  className={`country-bg border-2 border-gray-15 rounded-2 d-flex align-items-center justify-content-center text-white py-1 px-3`}
                >
                  <Row className="align-items-center">
                    <Col className="col-auto pe-0">
                      <CountryFlag countryCode={country} isDropDown={false} />
                    </Col>
                    <Col className="ps-0">
                      <AppDropDown
                        items={[
                          {
                            onClick: () => {
                              dispatch(setCountry(ECountry.KE));
                            },
                            text: (
                              <CountryFlag
                                countryCode={ECountry.KE}
                                countryName={translate(ECountry.KE)}
                              />
                            ),
                          },
                          {
                            onClick: () => {
                              dispatch(setCountry(ECountry.UG));
                            },
                            text: (
                              <CountryFlag
                                countryCode={ECountry.UG}
                                countryName={translate(ECountry.UG)}
                              />
                            ),
                          },
                          {
                            onClick: () => {
                              dispatch(setCountry(ECountry.TZ));
                            },
                            text: (
                              <CountryFlag
                                countryCode={ECountry.TZ}
                                countryName={translate(ECountry.TZ)}
                              />
                            ),
                          },
                        ]}
                        icon="arrow_drop_down"
                        showBackgroundAndBorder={false}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col>
                <Logo />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default PublicNavBar;
